import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import { CrossCircleIcon } from "../../components/Icons";
import { useState } from "react";

type ModalProps = {
    showModal: boolean;
    handleClose: () => void;
    handleDelete: () => void;
    isProcessing: boolean;
    errorMessage: string;
};

/**
 * 退会確認用モーダル
 *
 * @param ModalProps
 * @returns JSX.Element
 */
const AccountDeleteModal: React.FC<ModalProps> = ({
    showModal,
    handleClose,
    handleDelete,
    isProcessing,
    errorMessage,
}) => {
    const { t } = useTranslation();
    const [resetAgreed, setResetAgreed] = useState(false);

    /**
     * 退会同意チェックボックス用トグル
     */
    const handleCheckboxChange = () => {
        setResetAgreed(!resetAgreed);
    };

    return (
        <Modal
            className="fixed left-1/2 top-1/2 w-4/5 -translate-x-1/2 -translate-y-1/2 transform rounded-lg border bg-white p-4 shadow-md sm:w-1/2"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50"
            isOpen={showModal}
        >
            <div className="m-0 grid grid-cols-2 items-center justify-center p-4 sm:m-2">
                <div className="col-span-2 text-lg font-bold">{t("AccountSetting.accountDeleteModalTitle")}</div>
                <hr className="col-span-2 my-2 border-t-2"></hr>
                <div className="col-span-2 max-h-screen overflow-y-auto">
                    {/* エラーメッセージ */}
                    {errorMessage && (
                        <div className="flex items-center justify-center p-2">
                            <CrossCircleIcon />
                            <span className="pl-1 text-center font-bold text-red-600">
                                {errorMessage.split("\n").map((line, index) => (
                                    <div key={index}>{line}</div>
                                ))}
                            </span>
                        </div>
                    )}

                    {/* 注意書き */}
                    <div className="col-span-2">
                        <ul className="mx-auto ml-5 list-disc text-gray-700">
                            <li>{t("AccountSetting.accountDeleteWarning1")}</li>
                            <li>{t("AccountSetting.accountDeleteWarning2")}</li>
                            <li>{t("AccountSetting.accountDeleteWarning3")}</li>
                        </ul>
                    </div>

                    {/* 注意事項への同意 */}
                    <div className="col-span-2 flex items-center justify-evenly pt-2">
                        <label>
                            <input
                                type="checkbox"
                                checked={resetAgreed}
                                onChange={handleCheckboxChange}
                                className="mr-2"
                            />
                            {t("AccountSetting.accountDeleteAgree")}
                        </label>
                    </div>

                    {/* キャンセル/退会ボタン */}
                    <div className="col-span-2 mt-5 flex items-center justify-evenly">
                        <button className="btn-primary w-1/3" onClick={handleClose}>
                            {t("CommonLabel.cancel")}
                        </button>
                        <button
                            className={`w-1/3 rounded-lg py-2.5 text-center font-medium 
                            ${!resetAgreed && "bg-gray-300 text-gray-500"} 
                            ${
                                resetAgreed &&
                                !isProcessing &&
                                "bg-red-600 text-gray-50 hover:bg-red-800 active:bg-red-900 active:font-bold"
                            }
                            ${resetAgreed && isProcessing && "bg-red-900 text-gray-50"}
                            }`}
                            onClick={handleDelete}
                            disabled={!resetAgreed || isProcessing}
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center">
                                    <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                    <label className="">Processing...</label>
                                </div>
                            ) : (
                                t("AccountSetting.accountDelete")
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AccountDeleteModal;
